import pako from "pako";

function encodeRunLength(labels) {
    if (!labels) return [];
    let encoded = [];
    let count = 1;
    for (let i = 1; i < labels.length; i++) {
        if (labels[i] === labels[i - 1]) {
            count++;
        } else {
            encoded.push(count);
            encoded.push(labels[i - 1]);
            count = 1;
        }
    }
    encoded.push(count);
    encoded.push(labels[labels.length - 1]);
    return encoded;
}

// Export encodeRunLength
export { encodeRunLength };

function decodeRunLength(encoded) {
    let decoded = [];
    for (let i = 0; i < encoded.length; i += 2) {
        const count = encoded[i];
        const label = encoded[i + 1];
        for (let j = 0; j < count; j++) {
            decoded.push(label);
        }
    }
    return new Uint32Array(decoded);
}

// Export decodeRunLength
export { decodeRunLength };

/**
 * Decode a base64-encoded numpy array
 * @param {Object} encodedData - The encoded data with shape, dtype and base64 data
 * @returns {ArrayBuffer|Float32Array|Int32Array} - The decoded array
 */
function decodeBase64Array(encodedData) {
    if (!encodedData || !encodedData.data) return null;

    const { shape, dtype, data } = encodedData;
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    // Create the appropriate typed array based on dtype
    let typedArray;
    if (dtype.includes("float32")) {
        typedArray = new Float32Array(bytes.buffer);
    } else if (dtype.includes("int32")) {
        typedArray = new Int32Array(bytes.buffer);
    } else if (dtype.includes("float64")) {
        typedArray = new Float64Array(bytes.buffer);
    } else if (dtype.includes("int64")) {
        // JavaScript doesn't have Int64Array, so use BigInt64Array
        typedArray = new BigInt64Array(bytes.buffer);
    } else {
        // Default to generic ArrayBuffer
        return bytes.buffer;
    }

    // Reshape the array if necessary
    if (shape && shape.length > 1) {
        // For 2D arrays, we'll convert to nested arrays
        const result = [];
        const [rows, cols] = shape;
        for (let i = 0; i < rows; i++) {
            const row = [];
            for (let j = 0; j < cols; j++) {
                row.push(typedArray[i * cols + j]);
            }
            result.push(row);
        }
        return result;
    }

    return typedArray;
}

/**
 * Decode a nested dictionary with base64-encoded arrays
 * @param {Object} data - The encoded nested dictionary
 * @returns {Object} - The decoded nested dictionary
 */
function decodeNestedDictBase64(data) {
    if (!data) return null;

    const decoded = {};
    for (const [key, value] of Object.entries(data)) {
        if (
            value &&
            typeof value === "object" &&
            "data" in value &&
            "shape" in value &&
            "dtype" in value
        ) {
            // This is a base64-encoded array
            decoded[key] = decodeBase64Array(value);
        } else if (value && typeof value === "object") {
            // This could be a nested dictionary
            decoded[key] = decodeNestedDictBase64(value);
        } else {
            // This is a regular value
            decoded[key] = value;
        }
    }

    return decoded;
}

// Export decodeNestedDictBase64
export { decodeNestedDictBase64 };

/**
 * Decode keypoints that have been compressed with zlib and encoded as base64
 * @param {Object} compressedKeypoints - The compressed keypoints object
 * @returns {Array} - The decoded keypoints array
 */
function decodeCompressedKeypoints(compressedKeypoints) {
    if (!compressedKeypoints || !compressedKeypoints.data) return null;

    const { encoding, data } = compressedKeypoints;
    if (encoding !== "zlib+base64") {
        console.error("Unsupported keypoints encoding:", encoding);
        return null;
    }

    // Convert base64 to binary
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    // Decompress with pako (a JavaScript zlib implementation)
    try {
        // Import pako if it's not already imported
        if (typeof pako === "undefined") {
            throw new Error(
                "pako library is not available. Make sure to import it."
            );
        }

        // Use pako to decompress the data
        const decompressed = pako.inflate(bytes);

        // Convert Uint8Array to string
        const decompressedStr = new TextDecoder("utf-8").decode(decompressed);

        // Parse the JSON string
        const parsedData = JSON.parse(decompressedStr);

        // Check if the result is still compressed (has encoding and data fields)
        if (
            parsedData &&
            typeof parsedData === "object" &&
            parsedData.encoding &&
            parsedData.data
        ) {
            // Recursively decode the nested compressed data
            return decodeCompressedKeypoints(parsedData);
        }

        return parsedData;
    } catch (error) {
        console.error("Error decompressing keypoints:", error);
        return null;
    }
}

/**
 * Decode a ProcessingResult with all compressed fields
 * @param {Object} result - The compressed ProcessingResult from the server
 * @returns {Object} - The decoded ProcessingResult
 */
export function decodeProcessingResult(result) {
    if (!result) {
        return result;
    }

    // Make a deep copy for decoding
    result = JSON.parse(JSON.stringify(result));

    // Check if this is already decoded or not compressed
    if (!result.is_compressed) {
        return result; // Return as is if not compressed
    }

    // Decode using the is_compressed flag format
    try {
        // Decode labels if present
        if (result.labels && typeof result.labels === "object") {
            const encoding = result.labels.encoding;
            if (encoding === "runlength") {
                result.labels = decodeRunLength(result.labels.data);
            }
        } else if (result.labels && typeof result.labels === "string") {
            // For backward compatibility with old string format
            result.labels = decodeRunLength(JSON.parse(atob(result.labels)));
        }

        // Decode instance_ids if present
        if (result.instance_ids && typeof result.instance_ids === "object") {
            const encoding = result.instance_ids.encoding;
            if (encoding === "runlength") {
                result.instance_ids = decodeRunLength(result.instance_ids.data);
            }
        } else if (
            result.instance_ids &&
            typeof result.instance_ids === "string"
        ) {
            // For backward compatibility with old string format
            result.instance_ids = decodeRunLength(
                JSON.parse(atob(result.instance_ids))
            );
        }

        // Decode boundary_points if present
        if (
            result.boundary_points &&
            typeof result.boundary_points === "object"
        ) {
            result.boundary_points = decodeNestedDictBase64(
                result.boundary_points
            );
        } else if (
            result.boundary_points &&
            typeof result.boundary_points === "string"
        ) {
            // For backward compatibility with old string format
            result.boundary_points = JSON.parse(atob(result.boundary_points));
        }

        // Decode simplified_boundary_points if present
        if (
            result.simplified_boundary_points &&
            typeof result.simplified_boundary_points === "object"
        ) {
            result.simplified_boundary_points = decodeNestedDictBase64(
                result.simplified_boundary_points
            );
        } else if (
            result.simplified_boundary_points &&
            typeof result.simplified_boundary_points === "string"
        ) {
            // For backward compatibility with old string format
            result.simplified_boundary_points = JSON.parse(
                atob(result.simplified_boundary_points)
            );
        }

        // Decode keypoints if present
        if (result.keypoints && typeof result.keypoints === "object") {
            const encoding = result.keypoints.encoding;
            if (encoding === "zlib+base64") {
                result.keypoints = decodeCompressedKeypoints(result.keypoints);
            }
        } else if (result.keypoints && typeof result.keypoints === "string") {
            // For backward compatibility with old string format
            result.keypoints = JSON.parse(atob(result.keypoints));
        }

        // Decode reconstruction_result if present
        if (
            result.reconstruction_result &&
            typeof result.reconstruction_result === "object"
        ) {
            result.reconstruction_result = decodeNestedDictBase64(
                result.reconstruction_result
            );
        } else if (
            result.reconstruction_result &&
            typeof result.reconstruction_result === "string"
        ) {
            // For backward compatibility with old string format
            result.reconstruction_result = JSON.parse(
                atob(result.reconstruction_result)
            );
        }

        // Remove the is_compressed flag
        delete result.is_compressed;
    } catch (error) {
        console.error("Error decoding processing result:", error);
        console.error("Failed result structure:", result);
    }

    return result;
}

function encodeAnnotations(annotationClasses, labels, keypoints) {
    // Convert keypoint objects to snake_case format
    const encodedKeypoints =
        keypoints?.map((keypoint) => ({
            x: keypoint.x,
            y: keypoint.y,
            z: keypoint.z,
            class_name: keypoint.className,
            tooth_id: keypoint.toothId
        })) || [];
    // Encode labels using run-length encoding if labels exist
    const encodedLabels = labels ? encodeRunLength(labels) : [];
    let data = {
        version: "1.1",
        class_names: annotationClasses.map((c) => c.name),
        class_colors: annotationClasses.map((c) => c.color),
        class_ids: annotationClasses.map((c) => c.id),
        labels: encodedLabels,
        keypoints: encodedKeypoints,
        run_length_encoding: true
    };
    return data;
}

function decodeAnnotations(data) {
    if (data.run_length_encoding) {
        data.labels = decodeRunLength(data.labels);
    }

    // Add unique id to each keypoint if keypoints exist
    if (data.keypoints) {
        data.keypoints = data.keypoints.map((point) => ({
            x: point.x,
            y: point.y,
            z: point.z,
            className: point.class_name,
            pointName: `keypoint-point-${crypto.randomUUID()}`, // Add unique id,
            toothId: point.tooth_id || null
        }));
    }

    return data;
}

export { encodeAnnotations, decodeAnnotations };
